/**
 * Журнал приходов/отгрузок — таблица документов + сворачиваемые позиции.
 *
 * Используется для /warehouse/inflows и /warehouse/outflows.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { PeriodPicker } from "@/components/PeriodPicker";
import {
  fetchInflows, fetchOutflows, fetchInflow, fetchOutflow,
  formatRub,
  type Direction, type DocRow, type DocItem,
} from "@/api/warehouse";
import { ApiError } from "@/api/client";
import { usePeriod } from "@/state/period";


interface Props {
  direction: Direction;
}


export function WarehouseMovementsPage({ direction }: Props) {
  const { from, to } = usePeriod();
  const [warehouse, setWarehouse] = useState<"" | "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС">("");
  const [search, setSearch] = useState("");
  const [openedDocId, setOpenedDocId] = useState<number | null>(null);

  const list = useQuery({
    queryKey: [`warehouse-${direction}-list`, from, to, warehouse, search],
    queryFn: () => (direction === "inflow" ? fetchInflows : fetchOutflows)({
      date_from: from, date_to: to,
      warehouse: warehouse || undefined,
      search: search || undefined,
      limit: 500,
    }),
  });
  const docQ = useQuery({
    queryKey: [`warehouse-${direction}-doc`, openedDocId],
    queryFn: () => (direction === "inflow" ? fetchInflow : fetchOutflow)(openedDocId!),
    enabled: openedDocId !== null,
  });

  if (list.isLoading) return <Spinner size="lg" label="Загрузка журнала…" />;
  if (list.error) {
    const err = list.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }
  const data = list.data!;
  const isOutflow = direction === "outflow";
  const counterpartyLabel = isOutflow ? "Покупатель" : "Поставщик";

  return (
    <div className="page">

      <div className="filter-bar" style={{ justifyContent: "space-between", marginBottom: "var(--gap)", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select className="input-sm" value={warehouse} onChange={e => setWarehouse(e.target.value as "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС" | "")}>
            <option value="">Все склады</option>
            <option value="СКЛАД 2_4">СКЛАД 2_4</option>
            <option value="ИНСТРУМЕНТ 1.0">ИНСТРУМЕНТ 1.0</option>
            <option value="ОФИС">ОФИС</option>
          </select>
          <input
            type="search" className="input-sm"
            placeholder={`Поиск по № накладной / ${counterpartyLabel.toLowerCase()} / сотруднику`}
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ minWidth: 320 }}
          />
        </div>
        <PeriodPicker />
      </div>

      <div className="row row-3" style={{ marginBottom: "var(--gap)" }}>
        <div className="kpi">
          <div className="kpi-label">Документов</div>
          <div className="kpi-value kpi-value-sm">{data.total.toLocaleString("ru-RU")}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>в выборке</span></div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Показано</div>
          <div className="kpi-value kpi-value-sm">{data.items.length}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>топ {Math.min(500, data.total)}</span></div>
        </div>
        <div className={`kpi ${isOutflow ? "alarm" : "good"}`}>
          <div className="kpi-label">Сумма за период</div>
          <div className="kpi-value kpi-value-sm">
            {formatRub(data.sum_amount, { unit: false })}
            <span className="kpi-unit">{Math.abs(data.sum_amount) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
        </div>
      </div>

      {data.items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "32px 20px" }}>
          <div className="muted">Документов не найдено в периоде</div>
        </div>
      ) : (
        <div className="card">
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Дата</th>
                  <th style={{ width: 100 }}>№</th>
                  <th>Склад</th>
                  <th>{counterpartyLabel}</th>
                  <th>Сотрудник</th>
                  <th className="num" style={{ width: 80 }}>Поз.</th>
                  <th className="num" style={{ width: 130 }}>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(d => (
                  <DocRowComponent
                    key={d.id} doc={d} isOpened={openedDocId === d.id}
                    direction={direction}
                    onToggle={() => setOpenedDocId(openedDocId === d.id ? null : d.id)}
                    items={openedDocId === d.id ? docQ.data?.items : undefined}
                    loading={openedDocId === d.id && docQ.isLoading}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function DocRowComponent({
  doc, isOpened, onToggle, items, loading, direction,
}: {
  doc: DocRow;
  isOpened: boolean;
  onToggle: () => void;
  items: DocItem[] | undefined;
  loading: boolean;
  direction: Direction;
}) {
  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={onToggle}>
        <td className="tabular">{doc.doc_date || "—"}</td>
        <td className="tabular strong">№{doc.doc_number || "—"}</td>
        <td>{doc.warehouse || "—"}</td>
        <td>{doc.contractor_name || <span className="muted">—</span>}</td>
        <td>{doc.employee_name || <span className="muted">—</span>}</td>
        <td className="num">{doc.items_count}</td>
        <td className="num strong tabular">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {formatRub(doc.items_amount)}
            <Icon name={isOpened ? "chevron_d" : "chevron_r"} size={10}/>
          </span>
        </td>
      </tr>
      {isOpened && (
        <tr>
          <td colSpan={7} style={{ background: "var(--paper)", padding: "8px 16px" }}>
            {loading ? (
              <div className="muted">Загрузка позиций…</div>
            ) : !items?.length ? (
              <div className="muted">Позиций нет</div>
            ) : (
              <table className="tbl" style={{ marginTop: 4 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Наименование</th>
                    <th style={{ width: 110 }}>Артикул</th>
                    <th style={{ width: 60 }}>Ед.</th>
                    <th className="num" style={{ width: 80 }}>Кол-во</th>
                    <th className="num" style={{ width: 100 }}>
                      {direction === "inflow" ? "Цена закуп" : "Цена прод."}
                    </th>
                    {direction === "outflow" && <th className="num" style={{ width: 100 }}>Цена закуп</th>}
                    <th className="num" style={{ width: 110 }}>Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(i => (
                    <tr key={i.id}>
                      <td className="tabular">{i.line_no ?? "—"}</td>
                      <td>{i.name}</td>
                      <td className="tabular">{i.sku || "—"}</td>
                      <td>{i.unit || "—"}</td>
                      <td className="num tabular">{Math.round(i.quantity * 100) / 100}</td>
                      <td className="num tabular">
                        {direction === "inflow"
                          ? (i.price_buy != null ? formatRub(i.price_buy) : "—")
                          : (i.price != null ? formatRub(i.price) : "—")}
                      </td>
                      {direction === "outflow" && (
                        <td className="num tabular" style={{ color: "var(--mute)" }}>
                          {i.price_buy_at_sale != null ? formatRub(i.price_buy_at_sale) : "—"}
                        </td>
                      )}
                      <td className="num strong tabular">{i.amount != null ? formatRub(i.amount) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}


export function WarehouseInflowsPage() { return <WarehouseMovementsPage direction="inflow" />; }
export function WarehouseOutflowsPage() { return <WarehouseMovementsPage direction="outflow" />; }
